import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { StockService } from '../../stock/stock.service';
import { SupplierService } from '../../supplier/supplier.service';
import { SupplierProfile } from '../../supplier/supplier.type';

interface ImportResult { email: string; ok: boolean; reason?: string; }

/**
 * Bulk-add accounts from a set of login-info emails.
 *
 * This replaces the old "import to stocks for a date" flow. There is no date and
 * no initial-coins field: an account gets one persistent stock, it starts
 * NotReady, and the fee is the admin default snapshotted server-side.
 *
 * Emails are sent one per request because one stock per account is enforced by a
 * unique index — an email that already has a stock comes back 409 and is
 * reported on its own row rather than failing the whole batch.
 */
@Component({
  selector: 'app-import-to-stocks-dialog',
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule,
    MatInputModule, MatSelectModule, MatProgressBarModule, MatIconModule,
  ],
  templateUrl: './import-to-stocks-dialog.html',
  styleUrl: './import-to-stocks-dialog.scss',
})
export class ImportToStocksDialog {
  suppliers: SupplierProfile[] = [];
  supplierId = '';
  coinsToBeRemain = 0;

  running = false;
  done = 0;
  results: ImportResult[] = [];

  constructor(
    public ref: MatDialogRef<ImportToStocksDialog>,
    private stockService: StockService,
    private supplierService: SupplierService,
    @Inject(MAT_DIALOG_DATA) public data: { emails: string[] },
  ) {
    this.supplierService.listSuppliers({ pageSize: 200 }).subscribe({
      next: res => this.suppliers = res.suppliers,
      error: () => { },
    });
  }

  get total(): number { return this.data.emails.length; }
  get created(): number { return this.results.filter(r => r.ok).length; }
  get failed(): ImportResult[] { return this.results.filter(r => !r.ok); }
  get progress(): number { return this.total ? (this.done / this.total) * 100 : 0; }

  async run(): Promise<void> {
    this.running = true;
    this.results = [];
    this.done = 0;

    for (const email of this.data.emails) {
      try {
        await new Promise<void>((resolve, reject) => {
          this.stockService.createStock({
            login_info_email: email,
            supplier_id: this.supplierId || undefined,
            coins_to_be_remain: this.coinsToBeRemain,
          }).subscribe({ next: () => resolve(), error: reject });
        });
        this.results.push({ email, ok: true });
      } catch (err: any) {
        this.results.push({ email, ok: false, reason: err?.error?.error ?? 'failed' });
      }
      this.done++;
    }

    this.running = false;
  }

  close(): void {
    this.ref.close(this.created > 0);
  }
}
